import { useEffect, useState } from 'react'
import { KeycloakAdminService, KeycloakService } from '@nv/auth'
import { BackBtn, Loader, NVButton, TextField } from '@nv/basic-components'
import { AlertBanner, PasswordValidate } from '@nv/components'
import { CheckAnimationIcon } from '@nv/images/design/check-animation-icon'
import { AdminVoterAuthService, VoterAuthService } from '@nv/services'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import * as Yup from 'yup'
import styles from './ChangePassword.module.scss'
import '@djthoms/pretty-checkbox'

const initialValues = {
  currentPassword: '',
  password: '',
  confirmPassword: '',
}

export const ChangePassword = ({ isAdmin = false }) => {
  const { t } = useTranslation()
  const [authError, setAuthError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [changePassSuccess, setChangePassSuccess] = useState(false)
  const [validationResult, setValidationResult] = useState(false)
  const location = useLocation()
  const flow = location?.state?.flow
  const hideBackButton = location.pathname.includes('auth')

  const passwordSchema = Yup.object().shape({
    currentPassword: Yup.string().required(t('auth.password.change.validations.currentReq')),
    password: Yup.string().required(t('auth.password.change.validations.newReq')),
    confirmPassword: Yup.string()
      .required(t('auth.password.change.validations.confirmReq'))
      .when('password', {
        is: (val: string) => (val && val.length > 0 ? true : false),
        then: Yup.string().oneOf([Yup.ref('password')], t('auth.password.change.validations.match')),
      }),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: passwordSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true)
      setAuthError(false)

      const userInput = {
        currentPassword: values.currentPassword,
        password: values.password,
        confirmPassword: values.confirmPassword,
      }

      try {
        let updated

        if (isAdmin) {
          if (KeycloakAdminService?.isLoggedIn()) {
            updated = await AdminVoterAuthService.updateUserPassword(userInput.currentPassword, userInput.password)
          } else if (flow === 'Onboarding') {
            updated = await AdminVoterAuthService.updateUserPasswordOnboard(userInput.currentPassword, userInput.password)
          } else if (flow === 'Forgot Password' || flow === 'Admin Forgot Password') {
            updated = await AdminVoterAuthService.updateUserPasswordResetPasswordFlow(userInput.currentPassword, userInput.password)
          }
        } else {
          if (KeycloakService?.isLoggedIn()) {
            updated = await VoterAuthService.updateUserPassword(userInput.currentPassword, userInput.password)
          } else {
            updated = await VoterAuthService.updateUserPasswordResetPasswordFlow(userInput.currentPassword, userInput.password)
          }
        }

        if (updated?.success) {
          setChangePassSuccess(true)
          formik.resetForm()
        } else {
          setAuthError(true)
        }
      } catch (error) {
        // Handle error
      } finally {
        setLoading(false)
      }
    },
  })

  useEffect(() => {
    setAuthError(false)
  }, [formik.values.currentPassword])

  return (
    <div className={styles['container']}>
      {!hideBackButton && (
        <div className={styles['back']}>
          <BackBtn />
        </div>
      )}

      {changePassSuccess && <AlertBanner icon={<CheckAnimationIcon />} text={t('auth.password.change.success')} success={true} />}
      <div className={styles['form']}>
        <span className={styles['title']}>{t('auth.password.change.title')}</span>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            formik.submitForm()
          }}
        >
          <div className={styles['form__section']}>
            <TextField
              formik={formik}
              type='password'
              field='currentPassword'
              placeholder={t('auth.password.change.labels.current')}
              label={t('auth.password.change.labels.current')}
              isRequired={true}
              isPassword={true}
            />
          </div>
          <div className={styles['form__section']}>
            <TextField
              formik={formik}
              type='password'
              field='password'
              placeholder={t('auth.password.change.labels.new')}
              label={t('auth.password.change.labels.new')}
              isRequired={true}
              isPassword={true}
            />
          </div>
          <div className={styles['form__section']}>
            <TextField
              formik={formik}
              type='password'
              field='confirmPassword'
              placeholder={t('auth.password.change.labels.confirmNew')}
              label={t('auth.password.change.labels.confirmNew')}
              isRequired={true}
              isPassword={true}
            />
          </div>
          {authError && (
            <div className={styles['error-text']} role='alert'>
              {t('auth.password.change.errors.invalidCredentials')}
            </div>
          )}

          <PasswordValidate
            view={isAdmin ? 'admin' : 'voter'}
            formik={formik}
            result={(value) => {
              setValidationResult(value)
            }}
          />
        </form>

        <div className={styles['footer']}>
          {loading ? (
            <Loader />
          ) : (
            <NVButton onClick={() => formik.submitForm()} height={50} disabled={!validationResult} text={t('buttons.save')} />
          )}
        </div>
      </div>
    </div>
  )
}
